import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Save, GripVertical, ChevronUp, ChevronDown, Loader2,
  Eye, EyeOff, LayoutDashboard, Check
} from 'lucide-react';
import api from '../../utils/api';


const SECTION_ICONS = {
  hero: LayoutDashboard,
  sponsored_campaigns: Eye,
  trending_treatments: Eye,
  trusted_clinics: Eye,
  most_chosen_clinics: Eye,
  trust_content: Eye,
  certifications: Eye,
  how_it_works: Eye,
  testimonials: Eye,
  final_cta: Eye,
};

export default function AdminLandingSections() {
  const navigate = useNavigate();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);

  const getToken = () => '';

  const fetchSections = useCallback(async () => {
    try {
      const res = await api.get(`/admin/landing/sections`, {
      });
      const data = res.data;
      setSections((data.sections || []).sort((a, b) => a.display_order - b.display_order));
    } catch (err) {
      console.error('[AdminSections]', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSections(); }, [fetchSections]);

  const updateField = (idx, field, value) => {
    setSections(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
    setDirty(true);
    setSaved(false);
  };

  const moveSection = (idx, direction) => {
    setSections(prev => {
      const next = [...prev];
      const targetIdx = idx + direction;
      if (targetIdx < 0 || targetIdx >= next.length) return prev;
      // Swap display_order values
      const tempOrder = next[idx].display_order;
      next[idx] = { ...next[idx], display_order: next[targetIdx].display_order };
      next[targetIdx] = { ...next[targetIdx], display_order: tempOrder };
      // Sort by display_order
      return next.sort((a, b) => a.display_order - b.display_order);
    });
    setDirty(true);
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = sections.map(s => ({
        section_key: s.section_key,
        enabled: s.enabled,
        display_order: s.display_order,
        title_override: s.title_override || '',
        subtitle_override: s.subtitle_override || '',
      }));

      const res = await api.post(`/admin/landing/sections/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates }),
      });
      const data = res.data;
      setSections((data.sections || []).sort((a, b) => a.display_order - b.display_order));
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('[AdminSections] Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="admin-sections-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#3BA4C5]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-landing-sections">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/treatx-admin/dashboard')} data-testid="back-btn">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-[#122033]">Landing Page Sections</h1>
              <p className="text-sm text-slate-500">Configure visibility and order of V2 landing page sections</p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={!dirty || saving}
            className={`gap-2 ${saved ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-[#3BA4C5] hover:bg-[#2d8aab]'}`}
            data-testid="save-sections-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : saved ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
            {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
          </Button>
        </div>

        {/* Section List */}
        <div className="space-y-3">
          {sections.map((section, idx) => {
            const SIcon = SECTION_ICONS[section.section_key] || Eye;
            return (
              <Card key={section.section_key} className={`border transition-all ${section.enabled ? 'border-slate-200' : 'border-slate-200 opacity-60'}`}
                data-testid={`section-card-${section.section_key}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Drag handle / order controls */}
                    <div className="flex flex-col items-center gap-0.5">
                      <button onClick={() => moveSection(idx, -1)} disabled={idx === 0}
                        className="h-6 w-6 rounded hover:bg-slate-100 flex items-center justify-center disabled:opacity-30"
                        data-testid={`move-up-${section.section_key}`}>
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <GripVertical className="h-4 w-4 text-slate-300" />
                      <button onClick={() => moveSection(idx, 1)} disabled={idx === sections.length - 1}
                        className="h-6 w-6 rounded hover:bg-slate-100 flex items-center justify-center disabled:opacity-30"
                        data-testid={`move-down-${section.section_key}`}>
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Section info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="text-[10px] font-mono px-1.5">
                          #{section.display_order}
                        </Badge>
                        <span className="text-sm font-semibold text-[#122033]">
                          {section.section_key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </span>
                        {!section.enabled && (
                          <Badge variant="secondary" className="text-[9px]"><EyeOff className="h-3 w-3 mr-1" />Hidden</Badge>
                        )}
                      </div>

                      {/* Editable fields */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5 block">Title Override</label>
                          <Input
                            value={section.title_override || ''}
                            onChange={e => updateField(idx, 'title_override', e.target.value)}
                            placeholder="Default title"
                            className="h-8 text-sm"
                            data-testid={`title-input-${section.section_key}`}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5 block">Subtitle Override</label>
                          <Input
                            value={section.subtitle_override || ''}
                            onChange={e => updateField(idx, 'subtitle_override', e.target.value)}
                            placeholder="Default subtitle"
                            className="h-8 text-sm"
                            data-testid={`subtitle-input-${section.section_key}`}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Toggle */}
                    <div className="flex flex-col items-center gap-1">
                      <Switch
                        checked={section.enabled}
                        onCheckedChange={(checked) => updateField(idx, 'enabled', checked)}
                        data-testid={`toggle-${section.section_key}`}
                      />
                      <span className="text-[9px] text-slate-400">{section.enabled ? 'Visible' : 'Hidden'}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {sections.length === 0 && (
          <div className="text-center py-16 text-slate-400" data-testid="no-sections">
            <p className="text-lg font-medium">No sections configured</p>
            <p className="text-sm">Section configuration will be seeded on first landing page load.</p>
          </div>
        )}
      </div>
    </div>
  );
}
